import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Clock, TrendingUp } from 'lucide-react';

const ProductAnalytics = ({ timeRange }) => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/dashboard/product-analytics?time_range=${timeRange}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse bg-white/50 rounded-2xl h-96"></div>;
  }

  if (!analyticsData) return null;

  const COLORS = ['#fb7185', '#f472b6', '#e879f9', '#c084fc', '#a78bfa', '#818cf8'];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Find max value for heatmap color scaling
  const maxActivity = Math.max(...analyticsData.activity_heatmap.flat());

  const getHeatmapColor = (value) => {
    if (value === 0) return 'bg-gray-100';
    const intensity = value / maxActivity;
    if (intensity < 0.2) return 'bg-rose-100';
    if (intensity < 0.4) return 'bg-rose-200';
    if (intensity < 0.6) return 'bg-rose-300';
    if (intensity < 0.8) return 'bg-rose-400';
    return 'bg-rose-500';
  };

  return (
    <div className="space-y-6">
      {/* Top Features */}
      <div className="relative overflow-hidden rounded-2xl p-8 backdrop-blur-xl"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)',
             border: '1px solid rgba(255, 182, 193, 0.3)',
           }}>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-rose-500 to-pink-600 bg-clip-text text-transparent mb-6">
          📊 Top Features
        </h3>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={analyticsData.features} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
            <XAxis type="number" />
            <YAxis dataKey="name" type="category" width={150} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                border: '1px solid rgba(251, 113, 133, 0.3)',
                borderRadius: '12px'
              }}
            />
            <Bar dataKey="count" radius={[0, 8, 8, 0]}>
              {analyticsData.features.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl flex items-center gap-3">
          <Clock className="w-6 h-6 text-blue-500" />
          <div>
            <p className="text-sm text-gray-600">Average Session Time</p>
            <p className="text-2xl font-bold text-gray-900">{analyticsData.avg_session_time}</p>
          </div>
        </div>
      </div>

      {/* Activity Heatmap */}
      <div className="relative overflow-hidden rounded-2xl p-8 backdrop-blur-xl"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)',
             border: '1px solid rgba(255, 182, 193, 0.3)',
           }}>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-rose-500 to-pink-600 bg-clip-text text-transparent mb-6">
          🔥 Peak Activity Hours
        </h3>

        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            {/* Hour labels */}
            <div className="flex mb-2">
              <div className="w-20"></div>
              {[0, 3, 6, 9, 12, 15, 18, 21].map(hour => (
                <div key={hour} className="flex-1 text-center text-xs text-gray-600 min-w-[40px]">
                  {hour}:00
                </div>
              ))}
            </div>

            {/* Heatmap grid */}
            {days.map((day, dayIndex) => (
              <div key={day} className="flex mb-1">
                <div className="w-20 flex items-center">
                  <span className="text-sm font-medium text-gray-700">{day}</span>
                </div>
                <div className="flex-1 flex gap-1">
                  {analyticsData.activity_heatmap[dayIndex].map((value, hourIndex) => (
                    <div
                      key={hourIndex}
                      className={`flex-1 h-10 rounded ${getHeatmapColor(value)} transition-all hover:scale-110 cursor-pointer min-w-[10px]`}
                      title={`${day} ${hourIndex}:00 - ${value} activities`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-rose-100 rounded"></div>
            <span>Low</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-rose-300 rounded"></div>
            <span>Medium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-rose-500 rounded"></div>
            <span>High</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductAnalytics;